import {Breadcrumb, Button, message, Spin, Upload} from "antd";
import {UploadOutlined} from "@ant-design/icons";
import {DndProvider} from "react-dnd";
import {HTML5Backend} from "react-dnd-html5-backend";
import {useEffect, useState} from "react";
import AntdFileManager from "./filemanager/AntdFileManager";
import {useApi} from "../ApiProvider";
import {SearchResult} from "../../generated/api";
import {EditableTags} from "./filemanager/EditableTags";

export function FileManager() {
    const api = useApi();
    const [files, setFiles] = useState<any[]>([]);
    const [uploading, setUploading] = useState(false);
    const [uploadKey, setUploadKey] = useState(0);
    const [currentPath, setCurrentPath] = useState<string>("");

    let uploadTimeout: NodeJS.Timeout;

    useEffect(() => {
        loadFiles();
    }, []);

    const visibleFiles = files.filter((item) => {
        const parent = item.path?.split("/").slice(0, -1).join("/") || "";
        return parent === currentPath;
    });

    const loadFiles = async () => {
        const res = await api.getAllFilesFromRAG();
        setFiles(rebuildFileTree(res.data));
    };

    const handleUpload = async (fileList: any[]) => {
        setUploading(true);
        console.log('uploading...');

        try {
            const seen = new Set<string>();
            const promises: Promise<any>[] = [];

            for (const fileWrapper of fileList) {
                const file = fileWrapper.originFileObj;
                const relPath = (file as any).webkitRelativePath;

                if (
                    !file ||
                    !relPath ||
                    seen.has(relPath) ||
                    relPath.endsWith('.DS_Store') // ✅ skip macOS system file
                ) {
                    continue;
                }

                seen.add(relPath);
                promises.push(api.uploadFile(file, relPath));
            }

            await Promise.all(promises);
            message.success("Upload complete");
            await loadFiles();
        } catch (err) {
            console.error("Upload failed", err);
            message.error("Some files failed to upload");
        } finally {
            setUploading(false);
        }
    };

    const handleUploadChange = (info: any) => {
        clearTimeout(uploadTimeout);

        // Wait for AntD to finish adding all files
        uploadTimeout = setTimeout(() => {
            handleUpload(info.fileList); // Call once, with full list
            setUploadKey((k) => k + 1);  // Reset Upload input for next use
        }, 200); // 100–200ms is usually enough
    };

    function rebuildFileTree(docs: SearchResult[]): any[] {
        const allDirs = new Set<string>();
        const fileItems: any[] = [];

        docs.forEach((doc) => {
            const parts = doc.path?.split("/") ?? [];

            // collect all intermediate folders
            for (let i = 1; i < parts.length; i++) {
                allDirs.add(parts.slice(0, i).join("/"));
            }

            // actual file
            fileItems.push({
                type: "file",
                path: doc.path,
                basename: parts[parts.length - 1],
                tags: doc.tags ?? [],
            });
        });

        // Build folder tag map: path -> Set of tags inside
        const folderTagMap: Record<string, Set<string>> = {};
        console.log(fileItems)

        fileItems.forEach((file) => {
            const segments = file.path?.split("/");
            for (let i = 1; i < segments.length; i++) {
                const folderPath = segments.slice(0, i).join("/");
                if (!folderTagMap[folderPath]) folderTagMap[folderPath] = new Set();
                (file.tags ?? []).forEach((tag: string) => folderTagMap[folderPath].add(tag));
            }
        });

        const dirItems = Array.from(allDirs).map((path) => ({
            type: "directory",
            path,
            basename: path.split("/").pop(),
            tags: Array.from(folderTagMap[path] ?? []), // ✅ aggregated tags
        }));

        return [...dirItems, ...fileItems];
    }

    return (
        <DndProvider backend={HTML5Backend}>
            <div style={{width: "100%", height: "100%", padding: 20}}>
                <Upload
                    directory
                    key={uploadKey}
                    multiple
                    showUploadList={false}
                    customRequest={() => {
                    }} // prevent default AntD behavior
                    beforeUpload={() => false} // fully manual control
                    onChange={(info) => handleUploadChange(info)}
                >
                    <Button icon={<UploadOutlined/>} style={{marginBottom: 16}}>
                        Upload Folder
                    </Button>
                </Upload>

                <Spin spinning={uploading} tip="Uploading files...">
                    <Breadcrumb
                        items={[
                            {
                                title: <a onClick={() => setCurrentPath("")}>Main</a>,
                            },
                            ...currentPath.split("/").filter(Boolean).map((segment, idx, arr) => {
                                const path = arr.slice(0, idx + 1).join("/");
                                return {
                                    title: (
                                        <a
                                            onClick={() => setCurrentPath(path)}
                                        >
                                            {segment}
                                        </a>
                                    ),
                                    key: path,
                                };
                            }),
                        ]}
                    />

                    <AntdFileManager
                        additionalColumns={[
                            {
                                title: "Tags",
                                render: (_, record) => <EditableTags record={record}
                                                                     onSave={(path, tags) => api.updateTags({
                                                                         path: path,
                                                                         tags: tags
                                                                     }).then((res) => loadFiles())}
                                />
                            }
                        ]}
                        onDeleteItems={async (items) => {
                            await Promise.all(
                                items.map((item) => api.deleteFiles({path: item.path}))
                            );
                            message.success("Deleted successfully");
                            await loadFiles();
                        }}
                        dataKey="file-tree"
                        dataSource={visibleFiles}
                        onClickItem={(item: any) => {
                            if (item.type === "directory") {
                                setCurrentPath(item.path);
                            }
                        }}
                    />
                </Spin>
            </div>
        </DndProvider>
    );
}


